/**
 * @file branches.api.js
 * @module BranchesAPI
 * @description HTTP API wrapper for branch related endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios"

export const BranchesAPI = {
    /**
     * Create a new branch.
     * @param {Object} branch - Branch payload.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to created branch.
     */
    createBranch: (branch, token = "") => {
        return axiosInstance(token).post(`/branches`, branch);
    },

    /**
     * Retrieve branches with pagination and optional query.
     * @param {number} limit - Items per page.
     * @param {number} page - Page number.
     * @param {string} query - Optional query string.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to branches list.
     */
    getBranches: (limit, page, query, token = "") => {
        return axiosInstance(token).get(`/branches?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Get branch by id.
     * @param {string} branchId - Branch identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to branch data.
     */
    getBranchById: (branchId, token = "") => {
        return axiosInstance(token).get(`/branches/${branchId}`);
    },

    /**
     * Update a branch by id.
     * @param {string} branchId - Branch identifier.
     * @param {Object} body - Updated branch payload.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to updated branch.
     */
    updateBranchById: (branchId, body, token = "") => {
        return axiosInstance(token).put(`/branches/${branchId}`, body);
    },

    /**
     * Delete a branch by id.
     * @param {string} branchId - Branch identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteBranchById: (branchId, token = "") => {
        return axiosInstance(token).delete(`/branches/${branchId}`);
    },

    /**
     * Get branches associated with an operator.
     * @param {string} operatorId - Operator identifier.
     * @param {number} limit - Items per page.
     * @param {number} page - Page number.
     * @param {string} query - Optional query string.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to operator branches.
     */
    getBranchesByOperatorId: (operatorId, limit, page, query, token = "") => {
        return axiosInstance(token).get(`/operators/${operatorId}/branches?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Get branches options for an operator (lightweight list).
     * @param {string} operatorId - Operator identifier.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to options.
     */
    getBranchesOptionsByOperatorId: (operatorId, token = "") => {
        return axiosInstance(token).get(`/operators/${operatorId}/branches-options`);
    },

    /**
     * Get the admin coverage areas and transfer reservations of a branch to plot them on a map.
     * @param {string} branchId - Branch identifier.
     * @param {string} query - Query string with `date` (YYYY-MM-DDtoYYYY-MM-DD) and optional `status`.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to { branch, areas, reservations, total, truncated }.
     */
    getBranchMap: (branchId, query, token = "") => {
        return axiosInstance(token).get(`/branches/${branchId}/map${query ? `?${query}` : ""}`);
    },

    /**
     * Delete many branches by payload.
     * @param {Object} body - Payload describing branches to delete.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteManyBranchesById: (body, token = "") => {
        return axiosInstance(token).post(`/bulk-delete-branches`, body)
    },

    /**
     * Retrieve general branches options.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to options list.
     */
    getBranchesOptions: (token = "") => {
        return axiosInstance(token).get(`/branches-options`);
    },

    /**
     * Get branches options to be used in filters, optionally scoped to an operator.
     * @param {string} operatorId - Optional operator id.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to filter options.
     */
    getBranchesOptionsToFilters: (operatorId, token = "") => {
        return axiosInstance(token).get(`/destinations-filters${operatorId ? `?operatorId=${operatorId}` : ""}`);
    },
}
